import { CellItem } from '@components/UI-Components/HierarchyWiseItemsTable/components/Row/Cell'
import useViewContext from '@modules/order/hooks/use-view-context'
import { QUANTITIES_AND_BOOK } from '@modules/order/stores/view/List/ListViewTableService'
import get from 'lodash/get'
import last from 'lodash/last'
import React, { FC, memo, useMemo } from 'react'
import { useObserver } from 'mobx-react-lite'
import isNumber from 'lodash/isNumber'
import { nav } from '@stores/nav'
import { getEnumValue } from '@utils/get-enum-value'
import { VIPClientType } from '@modules/common/models/enums/VIPClientType'
import useOrderContext from '@modules/order/hooks/use-order-context'
import FieldSkeleton from '../../../CellLoaders/FieldSkeleton'
import { OrderType } from '@modules/common/models/enums'
import HierarchyCell from '../../components/HierarchyCell'
import Typography from '@components/std-components/Typography'
import formatNumberByLocale from '@utils/format-number-by-locale'
import formatCurrencyValueByLocale from '@utils/format-currency-value-by-locale'
import { productPriceStore } from '@stores/productPricesStore'
import { ListUIStore } from '@modules/order/stores/view/List/ListUIStore'
import { QuantityType } from '@components/UI-Components/QuantityType/constants/QuantityType'

const HierarchyQuantityValue: FC<CellItem> = ({ rowData, column }) =>
  useObserver(function useHook() {
    const { uiStore } = useViewContext()
    const {
      uiStore: {
        dataStore: { getSummaryDoorOrder, summary },
        quantityTypeStore: { type }
      }
    } = useOrderContext()
    const {
      queryParams: { clientType, orderType }
    } = nav
    const hierarchy = uiStore.getHierarchy(rowData.name)
    const { isValueSelected } = uiStore as ListUIStore
    const { field } = column
    const doorOrder = getSummaryDoorOrder(field)
    const priceData = productPriceStore?.getPriceForProduct(rowData?.id)

    const isRetail = useMemo(
      () => getEnumValue(VIPClientType.Internal) === clientType,
      [clientType]
    )

    const captureSize = useMemo(
      () => summary?.client?.captureSize || summary?.clientOrder?.client?.captureSize,
      [summary]
    )

    const kpiField = useMemo(() => {
      return field === QUANTITIES_AND_BOOK && orderType !== OrderType.Door
        ? `kpis`
        : `kpis.doorKpis.${doorOrder?.door?.id}`
    }, [field, doorOrder, orderType])
    const totalByDraft = useMemo(
      () =>
        hierarchy &&
        (get(
          last(hierarchy),
          `${kpiField}.total${isValueSelected ? 'Value' : ''}ByDraftQuantity`
        ) ??
          0),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [hierarchy, isValueSelected]
    )
    const totalBySize = useMemo(
      () =>
        hierarchy &&
        (get(
          last(hierarchy),
          `${kpiField}.total${isValueSelected ? 'Value' : ''}BySizeQuantity`
        ) ??
          0),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [hierarchy, isValueSelected]
    )

    /**
     * process currency-value string
     * show currency if switch is on Value
     * product qty type -> draft / size if captureSize enabled, else draft
     * size qty type -> size fields
     * door qty type -> draft fields
     */
    const qtyValueStr = useMemo(() => {
      let str = ''
      const currency = priceData?.kpiCurrency
        ? priceData?.kpiCurrency
        : priceData?.[isRetail ? 'rtlCurrency' : 'whlCurrency']
      if (isNumber(totalByDraft) && isNumber(totalBySize)) {
        switch (type) {
          case QuantityType.Product:
            str = captureSize
              ? `${
                  isValueSelected
                    ? formatCurrencyValueByLocale(currency, totalByDraft)
                    : formatNumberByLocale(totalByDraft)
                } / ${
                  isValueSelected
                    ? formatCurrencyValueByLocale(currency, totalBySize)
                    : formatNumberByLocale(totalBySize)
                }`
              : `${
                  isValueSelected
                    ? formatCurrencyValueByLocale(currency, totalByDraft)
                    : formatNumberByLocale(totalByDraft)
                }`
            break
          case QuantityType.Draft:
            str = `${
              isValueSelected
                ? formatCurrencyValueByLocale(currency, totalByDraft)
                : formatNumberByLocale(totalByDraft)
            }`
            break
          case QuantityType.DraftWHL:
            str = `${
              isValueSelected
                ? `${priceData?.[isRetail ? 'rtlCurrency' : 'whlCurrency']} `
                : ''
            }${formatNumberByLocale(totalByDraft)}`
            break
          case QuantityType.Size:
            str = `${
              isValueSelected
                ? formatCurrencyValueByLocale(currency, totalBySize)
                : formatNumberByLocale(totalBySize)
            }`
            break
        }
      }
      return str
    }, [
      totalByDraft,
      totalBySize,
      isRetail,
      isValueSelected,
      priceData,
      type,
      captureSize
    ])

    if (!rowData) {
      return (
        <HierarchyCell>
          <FieldSkeleton />
        </HierarchyCell>
      )
    }
    return (
      <HierarchyCell>
        <Typography variant='subHeading1'>{qtyValueStr}</Typography>
      </HierarchyCell>
    )
  })

export default memo(HierarchyQuantityValue)
